<template>
  <div class="taboos-page">
    <div class="container">
      <!-- 面包屑导航 -->
      <BreadcrumbNav />
      
      <h1 class="page-title">Name Sound and Form Taboos (字音字形避讳)</h1>
      <div class="subtitle">Avoiding characters that sound like or have similar forms to names of elders, celebrities, or inauspicious meanings</div>
      
      <div class="content-section">
        <div class="section-container">
          <div class="content-wrapper">
            <div class="article-content">
              <div class="intro-section">
                <p>In traditional Chinese culture, naming is an extremely important matter, and the sound and form taboos of characters are key elements that cannot be overlooked in the naming process. Correctly understanding and applying the principles of sound and form taboos can help avoid many cultural prohibitions and provide name choices that better conform to both cultural traditions and modern aesthetics.</p>
              </div>

              <section id="sound-taboos">
                <h2>I. Sound Taboos (字音避讳)</h2>
                
                <div class="subsection">
                  <h3>1. Homophone Taboos (谐音避讳)</h3>
                  <p>Chinese language has numerous homophones and near-homophones. When naming, one should avoid characters that sound like inauspicious words.</p>
                  <div class="example-box">
                    <h4>Common Homophone Taboo Examples:</h4>
                    <ul>
                      <li><span class="highlight">死(sǐ, death)</span>: Avoid characters that sound like "死", such as "思, 司, 四" and other fourth-tone characters</li>
                      <li><span class="highlight">穷(qióng, poor)</span>: Avoid characters that sound like "穷", such as "琼, 邛, 穹"</li>
                      <li><span class="highlight">苦(kǔ, bitter)</span>: Avoid characters that sound like "苦", such as "库, 酷"</li>
                      <li><span class="highlight">败(bài, defeat)</span>: Avoid characters that sound like "败", such as "拜, 稗"</li>
                      <li><span class="highlight">散(sàn, scatter)</span>: Avoid characters that sound like "散" (separation), such as "伞" (umbrella)</li>
                    </ul>
                  </div>
                </div>
                
                <div class="subsection">
                  <h3>2. Multiple Dialect Considerations (多方言考量)</h3>
                  <p>China has many dialects, and the same character may have very different pronunciations in different dialects. When naming, one should consider pronunciations in both the family's primary dialect and in Mandarin.</p>
                  <div class="example-box">
                    <h4>Dialect Difference Examples:</h4>
                    <ul>
                      <li>The character "福" (blessing) is pronounced "fú" in Mandarin with an auspicious meaning, but in some southern dialects, it sounds similar to "服丧" (mourning)</li>
                      <li>The character "娶" (to marry) is pronounced "qǔ" in Mandarin, but in some dialects, it sounds like "去" (to leave), potentially creating the unwanted association of "marrying then leaving"</li>
                      <li>The character "六" (six) is pronounced "liù" in Mandarin, but in Cantonese, it sounds like "落" (to fall)</li>
                    </ul>
                  </div>
                </div>
                
                <div class="subsection">
                  <h3>3. Avoiding Elders' Names (长辈名讳避讳)</h3>
                  <p>Traditionally, Chinese people place great importance on avoiding characters used in the names of elders, especially parents and grandparents, as a sign of respect.</p>
                  <div class="example-box">
                    <h4>Elder Name Taboo Practices:</h4>
                    <ul>
                      <li>Traditionally, children do not use characters identical to those in their parents' names</li>
                      <li>Even characters with similar pronunciations to elders' names are avoided</li>
                      <li>In modern society, this tradition has weakened, but is still followed in many families</li>
                    </ul>
                  </div>
                </div>
                
                <div class="subsection">
                  <h3>4. Avoiding Celebrity Names (名人名讳避讳)</h3>
                  <p>Historically, there was a tradition of avoiding the names of emperors and famous figures in China. While this tradition is no longer strictly followed in modern times, it is still reflected in certain contexts.</p>
                  <div class="example-box">
                    <h4>Historical Taboo Examples:</h4>
                    <ul>
                      <li>During the Han Dynasty, people avoided the character "邦" in Emperor Liu Bang's name, using "国" instead</li>
                      <li>During the Tang Dynasty, people avoided the character "世" in Emperor Li Shimin's name, replacing it with "代" or other characters</li>
                      <li>When a family has a famous person, later generations typically avoid using characters from that person's name</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section id="form-taboos">
                <h2>II. Form Taboos (字形避讳)</h2>
                
                <div class="subsection">
                  <h3>1. Radical Taboos (部首避讳)</h3>
                  <p>Certain radicals may have negative connotations in specific contexts and should be carefully considered when naming.</p>
                  <div class="example-box">
                    <h4>Common Taboo Radicals:</h4>
                    <ul>
                      <li><span class="highlight">疒 (disease radical)</span>: Related to illness, such as "疾, 痛, 瘦"</li>
                      <li><span class="highlight">歹 (evil radical)</span>: Associated with bad things or death, such as "歼, 残"</li>
                      <li><span class="highlight">辶 (walk radical)</span>: In some cases may suggest "leaving" or "departing," such as "逃, 逝"</li>
                      <li><span class="highlight">刀 (knife radical)</span>: Associated with sharpness and harm, judgment needed based on specific character meanings</li>
                    </ul>
                  </div>
                </div>
                
                <div class="subsection">
                  <h3>2. Character Structure Taboos (字形结构避讳)</h3>
                  <p>The structure of certain characters may be interpreted as inauspicious, especially when decomposed and interpreted separately.</p>
                  <div class="example-box">
                    <h4>Character Structure Taboo Examples:</h4>
                    <ul>
                      <li>The character "舍" can be decomposed into "人" and "舌," potentially suggesting "human tongue"</li>
                      <li>The character "破" contains "皮" and "文," potentially read as "skin split revealing patterns"</li>
                      <li>The character "圭" with one horizontal stroke on top plus "土" forms "圭," requiring attention to possible negative associations</li>
                      <li>The character "贫" consists of "分" and "贝," suggesting the separation from wealth</li>
                    </ul>
                  </div>
                </div>
                
                <div class="subsection">
                  <h3>3. Missing Strokes or Imbalance (笔画缺失或不平衡)</h3>
                  <p>Traditionally, characters in names should have complete strokes and balanced structures, avoiding incomplete or unbalanced characters.</p>
                  <div class="example-box">
                    <h4>Character Form Features to Note:</h4>
                    <ul>
                      <li>Characters lacking "口" (mouth) may be interpreted as unable to speak well</li>
                      <li>Characters lacking "心" (heart) may be seen as lacking emotions</li>
                      <li>Characters with severely unbalanced structures may suggest unstable personality</li>
                      <li>Overly simple characters (like 一, 二, 三) may be viewed as lacking depth</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section id="combination-taboos">
                <h2>III. Sound and Form Combination Taboos (字音字形组合避讳)</h2>
                
                <div class="subsection">
                  <h3>1. Overall Name Combinations (名字整体组合)</h3>
                  <p>Characters in a name, when combined, may create new meanings or homophones that need comprehensive consideration.</p>
                  <div class="example-box">
                    <h4>Combination Taboo Examples:</h4>
                    <ul>
                      <li>The combination "王冠" (Wang Guan), though positive in meaning, sounds like "亡官" (lost official position)</li>
                      <li>The combination "高明" (Gao Ming) might sound like "膏盲" (blindness)</li>
                      <li>The combination "马洪" (Ma Hong) sounds like "马虹" (Ma Hong), which has the connotation of "thin horse"</li>
                      <li>The combination "贾斌" (Jia Bin) might sound like "假币" (counterfeit money)</li>
                    </ul>
                  </div>
                </div>
                
                <div class="subsection">
                  <h3>2. Surname and Given Name Considerations (姓名联合考量)</h3>
                  <p>Names need to harmoniously match with surnames, avoiding any negative meanings when combined with the surname.</p>
                  <div class="example-box">
                    <h4>Surname-Name Combination Taboos:</h4>
                    <ul>
                      <li>Those with surname "胡" should avoid the given name "闹," as "胡闹" means "to make trouble"</li>
                      <li>Those with surname "温" should avoid the given name "柔," as "温柔" means "gentle" (which would be a common adjective)</li>
                      <li>Those with surname "赵" should avoid the given name "钱," as "找钱" means "to give change"</li>
                      <li>Those with surname "第" should avoid the given name "一," as "第一" means "number one"</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section id="modern-considerations">
                <h2>IV. Modern Society Taboo Considerations (现代社会的避讳考量)</h2>
                
                <div class="subsection">
                  <h3>1. International Considerations (国际化考量)</h3>
                  <p>In a globalized context, the pronunciation and meaning of names in other languages also need to be considered.</p>
                  <div class="example-box">
                    <h4>International Taboo Examples:</h4>
                    <ul>
                      <li>Avoid pronunciations that have negative meanings in English or other common languages</li>
                      <li>Consider whether the romanized spelling of the name is easy to write and remember</li>
                      <li>Avoid characters that might cause misunderstandings in international communication</li>
                    </ul>
                  </div>
                </div>
                
                <div class="subsection">
                  <h3>2. New Taboos in the Internet Age (网络时代的新避讳)</h3>
                  <p>The popularization of the internet and social media has brought new taboo considerations.</p>
                  <div class="example-box">
                    <h4>Modern Taboo Considerations:</h4>
                    <ul>
                      <li>Avoid names that sound like popular internet slang</li>
                      <li>Consider whether the name is easy to use on digital platforms</li>
                      <li>Avoid name combinations that could easily become internet memes</li>
                    </ul>
                  </div>
                </div>
                
                <div class="subsection">
                  <h3>3. Career Development Considerations (职业发展考量)</h3>
                  <p>Names may influence future career development and should be considered with foresight.</p>
                  <div class="example-box">
                    <h4>Career Taboo Suggestions:</h4>
                    <ul>
                      <li>Avoid characters that might evoke negative associations in specific industries</li>
                      <li>Consider whether the name is suitable for formal occasions and professional environments</li>
                      <li>Avoid overly fancy or hard-to-remember character combinations</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section id="practical-guidelines">
                <h2>V. Practical Naming Guidelines (实用命名指南)</h2>
                
                <div class="subsection">
                  <h3>1. Taboo Checklist (避讳检查清单)</h3>
                  <div class="checklist-box">
                    <h4>Comprehensive Check Before Naming:</h4>
                    <ul>
                      <li>Check if the name has any negative homophones in Mandarin</li>
                      <li>Check if there are any negative homophones in the family's dialect</li>
                      <li>Check if the character forms have any negative associations</li>
                      <li>Check if the name combination has any negative meanings</li>
                      <li>Check if the name conflicts with elders' or celebrities' names</li>
                      <li>Check if the name is appropriate in an international context</li>
                      <li>Consult multiple family members for different perspectives</li>
                    </ul>
                  </div>
                </div>
                
                <div class="subsection">
                  <h3>2. Balancing Tradition and Modernity (平衡传统与现代)</h3>
                  <p>While following traditional taboo principles, modern aesthetics and values can be incorporated.</p>
                  <div class="example-box">
                    <h4>Balancing Suggestions:</h4>
                    <ul>
                      <li>Respect core traditional taboos, such as not using characters identical to deceased persons' names</li>
                      <li>Some overly superstitious taboos can be moderately relaxed</li>
                      <li>Make balanced choices based on family traditions and personal preferences</li>
                      <li>Consider the practicality and adaptability of the name in modern society</li>
                    </ul>
                  </div>
                </div>
                
                <div class="subsection">
                  <h3>3. Professional Advice (专业建议)</h3>
                  <p>For families who value traditional naming, consulting professionals may be considered.</p>
                  <div class="service-box">
                    <h4>Professional Naming Services Can Provide:</h4>
                    <ul>
                      <li>Personalized taboo analysis based on family history</li>
                      <li>Comprehensive sound and form evaluation</li>
                      <li>Name selection based on birth date (八字) and Five Elements (五行)</li>
                      <li>Name suitability assessment in multicultural contexts</li>
                    </ul>
                  </div>
                </div>
              </section>

              <div class="conclusion">
                <p>Sound and form taboos are important components of traditional Chinese naming culture, reflecting the Chinese people's profound understanding and clever use of language and characters. In modern society, reasonable application of these taboo principles can help create a good name for a child that both conforms to traditional cultural spirit and meets modern social needs. Most importantly, a name should convey parents' good wishes, reflect the family's cultural heritage, and bring positive influence to the child's future life journey.</p>
              </div>
            </div>
          </div>

              <!-- Explore More 组件 -->
    <ExploreMore :currentPath="$route.path" />
        </div>
      </div>
    </div>
    

  </div>
</template>

<script>
import BreadcrumbNav from '@/components/BreadcrumbNav.vue';
import ExploreMore from '@/components/ExploreMore.vue';

export default {
  name: 'NameSoundFormTaboos',
  components: {
    BreadcrumbNav,
    ExploreMore
  },
  data() {
    return {
      // Data directly used in template
    }
  },
  methods: {
    // Potentially needed methods
  },
  mounted() {
    // Operations performed after page loading
    window.scrollTo(0, 0);  // Ensure page starts from the top
  }
}
</script>

<style scoped>
.taboos-page {
  padding-top: 70px;
  padding-bottom: 50px;
  background-color: #f8f9fa;
  min-height: 100vh;
  color: #333;
}

.container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 20px;
}

.page-title {
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 10px;
  color: #333;
}

.subtitle {
  text-align: center;
  font-size: 1.2rem;
  color: #666;
  margin-bottom: 40px;
}

.content-section {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.section-container {
  padding: 40px;
}

.content-wrapper {
  display: flex;
  flex-direction: column;
}

.article-content {
  flex: 1;
  line-height: 1.8;
}

.intro-section {
  font-size: 1.1rem;
  color: #555;
  margin-bottom: 30px;
  border-left: 4px solid #e60012;
  padding-left: 20px;
  background-color: rgba(230, 0, 18, 0.05);
  padding: 15px 20px;
  border-radius: 0 8px 8px 0;
}

section {
  margin-bottom: 40px;
}

h2 {
  font-size: 1.8rem;
  color: #333;
  margin-bottom: 20px;
  border-bottom: 2px solid #e60012;
  padding-bottom: 10px;
  display: inline-block;
}

.subsection {
  margin-bottom: 25px;
}

h3 {
  font-size: 1.4rem;
  color: #444;
  margin-bottom: 15px;
}

p {
  margin-bottom: 15px;
}

.example-box, .checklist-box, .service-box {
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: 20px;
  margin: 15px 0;
  border-left: 4px solid #e60012;
}

.example-box h4, .checklist-box h4, .service-box h4 {
  color: #e60012;
  margin-bottom: 10px;
  font-size: 1.1rem;
}

ul {
  padding-left: 20px;
  margin-bottom: 0;
}

li {
  margin-bottom: 8px;
  line-height: 1.6;
}

.highlight {
  color: #e60012;
  font-weight: bold;
}

.conclusion {
  margin-top: 40px;
  padding: 20px;
  background-color: rgba(230, 0, 18, 0.05);
  border-radius: 8px;
  font-size: 1.1rem;
  line-height: 1.8;
}

@media (max-width: 768px) {
  .section-container {
    padding: 25px;
  }
  
  .page-title {
    font-size: 2rem;
  }
  
  .subtitle {
    font-size: 1rem;
  }
  
  h2 {
    font-size: 1.5rem;
  }
  
  h3 {
    font-size: 1.2rem;
  }
  
  .intro-section, .conclusion {
    font-size: 1rem;
  }
}
</style> 